<template>
   <div ref="flowselTop" class="relative h-full max-w-md flex-1 pt-6 lg:pt-0" :class="{ 'pt-0': isEndBlock }">
      <transition name="fade">
         <div :key="`block-${currentBlockIndex}`" class="h-full">
            <!-- Block or end block -->
            <FlowEnd v-if="isEndBlock" :block="currentBlock as FlowselEndBlock" :flowStudy="flow.study" />
            <FlowBlock
               v-else
               :flow="flow"
               :block="currentBlock"
               :userBlockAnswer="userBlockAnswer"
               :isBackButtonDisplayed="isBackButtonDisplayed"
               @previous-click="$emit('previous-click')"
               @participation-reset="$emit('participation-reset')"
               @answer-submitted="
                  (response) => {
                     $emit('answer-submitted', response);
                  }
               "
            />
         </div>
      </transition>
   </div>
</template>

<script setup lang="ts">
import type { AnswerResp } from "@/data/api-layer/answersApi";
import { type FlowselBlock } from "@/components/flowsel/flowsel.type";
import type { FlowData } from "@/data/api-layer/flowsApi";
import type { FlowselEndBlock } from "@/components/flowsel/flowEnd/FlowEnd.vue";

// Props & emits
// -----------------------------------------
defineEmits(["previous-click", "answer-submitted", "participation-reset"]);

interface Props {
   blocks: FlowselBlock[];
   flow: FlowData;
   currentBlockIndex: number;
   // users EXISTING answer
   userBlockAnswer?: AnswerResp;
}

const props = defineProps<Props>();

// Computed
// -----------------------------------------
const currentBlock = computed<FlowselBlock>(() => props.blocks[props.currentBlockIndex]);

const isBackButtonDisplayed = computed(() => props.currentBlockIndex > 0);

const isEndBlock = computed(() => currentBlock.value.id === "end-block");

// Methods
// -----------------------------------------
const flowselTop = ref(null);

// Watchers
// -----------------------------------------
watch(
   () => props.currentBlockIndex,
   (newVal, prevVal) => {
      flowselTop.value.scrollIntoView({ behavior: "smooth", block: "start" });
   }
);
</script>

<style scoped lang="scss">
// force scrollbar to appear on touch devices (touch devices hide scrollbars by default)
::-webkit-scrollbar {
   appearance: none;
   width: 5px;
}
::-webkit-scrollbar-thumb {
   border-radius: 4px;
   background-color: rgba(0, 0, 0, 0.5);
   box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
}

.fade-enter-active {
   transition: opacity 0.3s ease-in-out 0.2s;
}
.fade-enter,
.fade-enter-from {
   opacity: 0;
}
// The 'leave-from' class is applied as soon as transition begins. For the slider, the exiting block is not animated,
// but we need to set opacity immediately to 0 before the transition begins, because otherwise sometimes the
// 'enter' transition will begin just before the exiting block has been hidden. That causes a flash in which
// the exiting block is visible, but has changed position because it has been pushed out of the way by the
// entering block (though this is still at opacity 0).
.fade-leave-from {
   opacity: 0;
}
</style>
